import { PERMISSION, TPermissionId } from '../constants/permissionIds';

/**
 * Which view ids open each screen: ANY one of them is enough.
 *
 * It is the screen's counterpart of the server's route map, kept as ONE table so the whole
 * policy can be read at once: the route gate, the menu and the brand mark all read it, so a
 * screen hidden from the menu is also the one the gate refuses.
 *
 * Longest prefix first, so a deeper route is read before the one it sits under. Presence,
 * loans, machines and the material actions of the Fila have ids of their own but no route of
 * their own: the screen that hosts them answers for the route, and the controls inside follow
 * their own id.
 */
const ROUTE_VIEW_PERMISSIONS: ReadonlyArray<readonly [string, ReadonlyArray<TPermissionId>]> = [
  ['/visao-executiva', [PERMISSION.INICIO_VIEW]],
  ['/ops', [PERMISSION.CARTEIRA_VIEW]],
  ['/mapa', [PERMISSION.MAPA_VIEW]],
  // The Fila also serves whoever follows the raw material: its pending items live there.
  ['/fila', [PERMISSION.FILA_VIEW, PERMISSION.MATERIAIS_VIEW]],
  ['/apontamento', [PERMISSION.APONTAMENTO_VIEW]],
  ['/balanceamento', [PERMISSION.TEMPOS_VIEW]],
  ['/curadoria-tempos', [PERMISSION.TEMPOS_VIEW]],
  ['/cronometragem', [PERMISSION.TEMPOS_VIEW]],
  ['/sequencias', [PERMISSION.TEMPOS_VIEW]],
  ['/tempos', [PERMISSION.TEMPOS_VIEW]],
  ['/simulador', [PERMISSION.SIMULADOR_VIEW]],
  ['/relatorios', [PERMISSION.RELATORIOS_VIEW]],
  // Staff only: a brand buyer reaches the Portal by the e-mail on the brand, never by an id.
  ['/portal', [PERMISSION.PORTAL_VIEW]],
  // The machines are a tab of the records: either id opens the screen, and each tab follows its own.
  ['/cadastros', [PERMISSION.CADASTROS_VIEW, PERMISSION.MAQUINAS_VIEW]],
  ['/settings', [PERMISSION.CONFIGURACOES_VIEW]],
  // The boilerplate demo: off the menu, and the server serves `examples` under Settings.
  ['/examples', [PERMISSION.CONFIGURACOES_VIEW]],
];

/** The home: exact, since every path starts with "/". */
const HOME_VIEW_PERMISSIONS: ReadonlyArray<TPermissionId> = [PERMISSION.INICIO_VIEW];

/**
 * Routes that reach everyone on purpose. The manual is how to use every subject, not one of
 * them: hiding it would leave the person without the page that explains why a screen is
 * missing (its blocks that read an area follow that area's id).
 */
export const UNGATED_ROUTES: ReadonlyArray<string> = ['/ajuda'];

const normalize = (route: string): string => (route.split('?')[0] ?? '').replace(/\/+$/, '');

/**
 * The view ids that open a route (any of them), or `null` when the route has none.
 *
 * Matches on a SEPARATOR so a route of a similar name never inherits another's ids: `/opsicao`
 * is not `/ops`. A route outside the table reaches everyone: hiding a screen is a convenience
 * here, while the server, which refuses writes on what it does not know, is the real lock.
 */
export function routeViewPermissions(route: string): ReadonlyArray<TPermissionId> | null {
  const path = normalize(route);
  if (path === '' || path === '/') {
    return HOME_VIEW_PERMISSIONS;
  }
  if (UNGATED_ROUTES.some((free) => path === free || path.startsWith(`${free}/`))) {
    return null;
  }
  const match = ROUTE_VIEW_PERMISSIONS.find(([prefix]) => path === prefix || path.startsWith(`${prefix}/`));
  return match?.[1] ?? null;
}
